import { useState, useEffect, useMemo, useRef } from 'react';
import { Search, Filter, X } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Lightbox } from './Lightbox';
import { getImages, getCategories } from '../lib/api';

const GRID_PAGE_SIZE = 12;

const toSafeLowerText = (value) => (typeof value === 'string' ? value.toLowerCase() : '');

const toSafeText = (value) => (typeof value === 'string' ? value : '');

export function ImageGallery({ showSearchControls = true, requireFilters = false }) {
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [visibleCount, setVisibleCount] = useState(GRID_PAGE_SIZE);
  const loadMoreRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [imagesData, categoriesData] = await Promise.all([
        getImages(),
        getCategories()
      ]);
      setImages(Array.isArray(imagesData) ? imagesData : []);
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredImages = images.filter((image) => {
    const title = toSafeLowerText(image?.title);
    const description = toSafeLowerText(image?.description);
    const normalizedSearchTerm = searchTerm.toLowerCase();

    const matchesSearch = searchTerm === '' ||
      title.includes(normalizedSearchTerm) ||
      description.includes(normalizedSearchTerm) ||
      image.tags?.some(tag => toSafeLowerText(tag).includes(normalizedSearchTerm));

    const matchesCategory = selectedCategory === 'all' || toSafeText(image?.category) === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const formatDisplayTitle = (title, category, index) => {
    const normalizedTitle = (title || '').trim();

    if (normalizedTitle === '') {
      const fallbackCategory = (category || 'Image').replace(/[-_]+/g, ' ');
      return `${fallbackCategory} ${index + 1}`.replace(/\b\w/g, (char) => char.toUpperCase());
    }

    const looksLikeGeneratedName =
      /^hf\s/i.test(normalizedTitle) ||
      /[0-9a-f]{8,}/i.test(normalizedTitle) ||
      normalizedTitle.length > 45;

    if (looksLikeGeneratedName) {
      return (category || 'Image').replace(/[-_]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
    }

    return normalizedTitle;
  };

  const orderedImages = [...filteredImages].sort((a, b) => {
    const tagKey = (image) => (image.tags || [])
      .map((tag) => tag.toLowerCase())
      .sort()
      .join('|');
    const keyA = tagKey(a) || a.category || '';
    const keyB = tagKey(b) || b.category || '';

    if (keyA === keyB) {
      return toSafeText(a.title).localeCompare(toSafeText(b.title));
    }

    return keyA.localeCompare(keyB);
  });

  const galleryImages = orderedImages.map((image, index) => ({
    ...image,
    title: formatDisplayTitle(image.title, image.category, index),
  }));

  const visibleImages = useMemo(
    () => galleryImages.slice(0, visibleCount),
    [galleryImages, visibleCount]
  );

  useEffect(() => {
    setVisibleCount(GRID_PAGE_SIZE);
  }, [searchTerm, selectedCategory]);

  useEffect(() => {
    if (!loadMoreRef.current || visibleCount >= galleryImages.length) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisibleCount((prev) => Math.min(prev + GRID_PAGE_SIZE, galleryImages.length));
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [visibleCount, galleryImages.length]);

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('all');
  };

  const hasActiveFilters = searchTerm !== '' || selectedCategory !== 'all';
  const shouldShowResults = !requireFilters || hasActiveFilters;

  const handleLightboxNavigate = (direction) => {
    if (lightboxIndex === null) return;
    if (direction === 'prev' && lightboxIndex > 0) {
      setLightboxIndex(lightboxIndex - 1);
    } else if (direction === 'next' && lightboxIndex < galleryImages.length - 1) {
      setLightboxIndex(lightboxIndex + 1);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-muted-foreground">Loading gallery...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {showSearchControls && (
        <section className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-center">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search images..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>

          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-full sm:w-48" data-testid="category-filter">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((category) => (
                <SelectItem key={category.id} value={category.slug}>
                  {category.name} ({category.image_count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-muted-foreground"
              data-testid="clear-filters"
            >
              <X className="h-4 w-4 mr-1" />
              Clear
            </Button>
          )}
        </section>
      )}

      {shouldShowResults && (
        <p className="text-sm text-muted-foreground text-center">
          {galleryImages.length} {galleryImages.length === 1 ? 'image' : 'images'} found
        </p>
      )}

      {shouldShowResults && galleryImages.length > 0 ? (
        <>
          <section
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4"
            data-testid="gallery-grid"
          >
            {visibleImages.map((image, index) => (
              <button
                key={image.id || `${image.url}-${index}`}
                onClick={() => setLightboxIndex(index)}
                className="group relative overflow-hidden rounded-lg border border-white/10 bg-black/40 aspect-[4/3] md:aspect-square"
                data-testid={`gallery-card-${index}`}
              >
                <img
                  src={image.thumbnail_url || image.url}
                  alt={image.title || 'Gallery image'}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                <div className="absolute bottom-2 left-2 right-2 text-left">
                  <p className="text-white text-sm md:text-base font-medium truncate">{image.title}</p>
                  <p className="text-white/70 text-xs capitalize truncate">{image.category}</p>
                </div>
              </button>
            ))}
          </section>

          {visibleCount < galleryImages.length && (
            <div ref={loadMoreRef} className="text-center py-4 text-xs text-muted-foreground" data-testid="gallery-load-more-trigger">
              Loading more images...
            </div>
          )}
        </>
      ) : shouldShowResults ? (
        <div className="text-center py-16">
          <p className="text-muted-foreground text-lg">No images found matching your criteria.</p>
          {hasActiveFilters && (
            <Button
              variant="outline"
              onClick={clearFilters}
              className="mt-4"
              data-testid="no-results-clear"
            >
              Clear filters
            </Button>
          )}
        </div>
      ) : (
        <div className="text-center py-16">
          <p className="text-muted-foreground text-lg">
            Start with a search or choose a category to see results.
          </p>
        </div>
      )}

      {lightboxIndex !== null && (
        <Lightbox
          images={galleryImages}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={handleLightboxNavigate}
        />
      )}
    </div>
  );
}
